import { RGBtoHSV, RGBtoLab } from './colorUtils';
import type { SkinAnalysisRegion } from './faceRoi';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SkinLabFeatures {
  /** Mean CIE L* across detected skin pixels (0–100) */
  meanL: number;
  /** Mean CIE a* (negative = green, positive = red) */
  meanA: number;
  /** Mean CIE b* (negative = blue, positive = yellow) */
  meanB: number;
  /** Population std dev of a* — proxy for chroma variation */
  stdA: number;
  /** Population std dev of b* — proxy for chroma variation */
  stdB: number;
  /** Number of skin pixels contributing to the stats */
  pixelCount: number;
}

export interface HairFeatures {
  /**
   * 25th-percentile CIE L* of non-skin pixels above the face.
   * Using the lower percentile instead of mean avoids bright background
   * (sky, wall) dominating the estimate.
   * Value is −1 when the hair band is empty or unreliable.
   */
  meanL: number;
  /**
   * Depth proxy: (100 − L*) / 100, normalised to 0–1.
   * 1 = very dark hair, 0 = very light hair.
   * −1 when unavailable.
   */
  depth: number;
  /** Number of non-skin pixels sampled; 0 means data is unavailable */
  pixelCount: number;
}

export interface FacialColorFeatures {
  skin: SkinLabFeatures;
  hair: HairFeatures;
  /**
   * undertone_score = skin.meanB − skin.meanA.
   * Positive → yellow dominates → warm; negative → red dominates → cool.
   */
  undertoneScore: number;
  /** skin.meanL / 100, a linear brightness proxy in 0–1 */
  brightness: number;
  /** √(meanA² + meanB²) — overall chroma/saturation magnitude */
  chroma: number;
  /**
   * |skin.meanL − hair.meanL|.  −1 when hair data is unavailable.
   * Large value = high contrast between skin and hair.
   */
  contrast: number;
}

// ─── Skin pixel gate ──────────────────────────────────────────────────────────
// Matches the inclusive HSV range used in SkinUndertoneService.analyzeSkinUndertoneAdvanced.

function isSkinPixel(h: number, s: number, v: number): boolean {
  return (
    s >= 0.03 &&
    s <= 0.95 &&
    v >= 0.1 &&
    v <= 0.99 &&
    ((h >= 0 && h <= 70) || (h >= 320 && h <= 360))
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

/**
 * Extract Lab-based color features from an RGBA ImageData frame.
 *
 * Skin pixels are identified with the same HSV gate as
 * `SkinUndertoneService.analyzeSkinUndertoneAdvanced` so that both code paths
 * agree on which pixels represent the face.
 *
 * Hair pixels are sampled from a band directly above the face bounding box.
 * Because the band may capture bright backgrounds (sky, walls) rather than
 * actual hair, we take the 25th-percentile L* of non-skin pixels, which
 * skews toward the darker end.  When fewer than 10 usable pixels are found
 * the hair fields are set to −1 / 0 to signal "unavailable", and the
 * classifier will fall back to chroma-only contrast estimation.
 *
 * Pixels are sampled every 3 px in each axis to balance accuracy and
 * performance; a 640×480 frame at stride 3 gives ~7 100 face-region samples.
 */
export function extractFacialColorFeatures(
  imageData: ImageData,
  faceRegion: SkinAnalysisRegion
): FacialColorFeatures {
  const { data: pixels, width, height } = imageData;

  // ── Face bounding box, clamped to image ──────────────────────────────────
  const x0 = Math.max(0, Math.floor(faceRegion.x));
  const y0 = Math.max(0, Math.floor(faceRegion.y));
  const x1 = Math.min(width - 1, Math.ceil(faceRegion.x + faceRegion.width) - 1);
  const y1 = Math.min(height - 1, Math.ceil(faceRegion.y + faceRegion.height) - 1);

  // ── Skin sampling inside face ROI ────────────────────────────────────────
  const skinLab: Array<{ L: number; a: number; b: number }> = [];

  for (let y = y0; y <= y1; y += 3) {
    for (let x = x0; x <= x1; x += 3) {
      const idx = (y * width + x) * 4;
      if (pixels[idx + 3]! < 128) continue;

      const r = pixels[idx]!;
      const g = pixels[idx + 1]!;
      const b = pixels[idx + 2]!;

      const hsv = RGBtoHSV(r, g, b);
      if (isSkinPixel(hsv.h, hsv.s, hsv.v)) {
        skinLab.push(RGBtoLab(r, g, b));
      }
    }
  }

  const skin = computeSkinFeatures(skinLab);

  // ── Hair band: top 60 % of face height, placed above the face ───────────
  const hairBandH = Math.round(faceRegion.height * 0.6);
  const hairY0 = Math.max(0, y0 - hairBandH);
  const hairY1 = y0 - 1;

  const hair =
    hairY1 >= hairY0
      ? computeHairFeatures(pixels, width, height, x0, x1, hairY0, hairY1)
      : { meanL: -1, depth: -1, pixelCount: 0 };

  const contrast =
    hair.meanL >= 0 ? Math.abs(skin.meanL - hair.meanL) : -1;

  return {
    skin,
    hair,
    undertoneScore: skin.meanB - skin.meanA,
    brightness: skin.meanL / 100,
    chroma: Math.sqrt(skin.meanA ** 2 + skin.meanB ** 2),
    contrast,
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function computeSkinFeatures(
  samples: Array<{ L: number; a: number; b: number }>
): SkinLabFeatures {
  if (samples.length === 0) {
    // Fallback defaults represent a "neutral medium" skin — the classifier
    // will see a near-zero undertone_score and mid-range brightness/chroma,
    // which will produce a lower-confidence result.
    return { meanL: 55, meanA: 8, meanB: 12, stdA: 5, stdB: 5, pixelCount: 0 };
  }

  const n = samples.length;
  const meanL = samples.reduce((s, p) => s + p.L, 0) / n;
  const meanA = samples.reduce((s, p) => s + p.a, 0) / n;
  const meanB = samples.reduce((s, p) => s + p.b, 0) / n;
  const stdA = Math.sqrt(
    samples.reduce((s, p) => s + (p.a - meanA) ** 2, 0) / n
  );
  const stdB = Math.sqrt(
    samples.reduce((s, p) => s + (p.b - meanB) ** 2, 0) / n
  );

  return { meanL, meanA, meanB, stdA, stdB, pixelCount: n };
}

function computeHairFeatures(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  x0: number,
  x1: number,
  hairY0: number,
  hairY1: number
): HairFeatures {
  const lValues: number[] = [];

  for (let y = hairY0; y <= hairY1; y += 3) {
    for (let x = x0; x <= x1; x += 3) {
      if (x < 0 || x >= width || y < 0 || y >= height) continue;
      const idx = (y * width + x) * 4;
      if (pixels[idx + 3]! < 128) continue;

      const r = pixels[idx]!;
      const g = pixels[idx + 1]!;
      const b = pixels[idx + 2]!;

      const hsv = RGBtoHSV(r, g, b);
      if (!isSkinPixel(hsv.h, hsv.s, hsv.v)) {
        lValues.push(RGBtoLab(r, g, b).L);
      }
    }
  }

  if (lValues.length < 10) {
    return { meanL: -1, depth: -1, pixelCount: 0 };
  }

  lValues.sort((a, b) => a - b);
  // 25th percentile skews toward actual hair rather than bright backgrounds.
  const p25 = lValues[Math.floor(lValues.length * 0.25)]!;

  return {
    meanL: p25,
    depth: Math.max(0, Math.min(1, (100 - p25) / 100)),
    pixelCount: lValues.length,
  };
}
